import { useIntl } from 'react-intl';

import { CMSSection } from '@/cms/CMSSection';
import type { ContentfulExhibitionListFragment } from '@/graphql/contentful/api';
import { messages as boutiqueMessages } from '@/messages/boutique';

import { PastExhibitionItemsContainer } from './components/PastExhibitionItemsContainer/PastExhibitionItemsContainer';
import { UpcomingExhibitionItemsContainer } from './components/UpcomingExhibitionItemsContainer/UpcomingExhibitionItemsContainer';

import * as S from './ExhibitionList.styles';
export type ExhibitionItem = NonNullable<
  NonNullable<
    ContentfulExhibitionListFragment['exhibitionItemsCollection']
  >['items'][number]
>;

export const ExhibitionCategory = ({
  categoryDescription,
  categoryTitle,
  categoryTitleSeoTag,
}: Pick<
  ContentfulExhibitionListFragment,
  'categoryTitle' | 'categoryDescription' | 'categoryTitleSeoTag'
>) => {
  const headingType = (categoryTitleSeoTag ??
    'h3') as keyof HTMLElementTagNameMap;

  return (
    <S.CategoryWrapper>
      <S.CategoryTitle as={headingType}>{categoryTitle}</S.CategoryTitle>
      <S.CategoryDescriptionWrapper>
        {categoryDescription}
      </S.CategoryDescriptionWrapper>
    </S.CategoryWrapper>
  );
};

export const ExhibitionList = (props: ContentfulExhibitionListFragment) => {
  const {
    categoryDescription,
    categoryTitle,
    categoryTitleSeoTag,
    exhibitionItemsCollection,
    exhibitionListLabel,
    loadMoreLabel,
    pastExhibitionsLabel,
  } = props;
  const { formatMessage } = useIntl();
  const now = new Date();

  const pastExhibitions = exhibitionItemsCollection?.items
    .filter((item) => item?.endDate && new Date(item.endDate) < now)
    .sort((a, b) => {
      if (!a?.startDate || !b?.startDate) return 0;
      return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
    });
  const upcomingExhibitions = exhibitionItemsCollection?.items
    .filter((item) => item?.endDate && new Date(item.endDate) >= now)
    .sort((a, b) => {
      if (!a?.startDate || !b?.startDate) return 0;
      return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
    });
  return (
    <CMSSection>
      {categoryTitle && categoryDescription && (
        <ExhibitionCategory
          categoryDescription={categoryDescription}
          categoryTitle={categoryTitle}
          categoryTitleSeoTag={categoryTitleSeoTag}
        />
      )}
      {upcomingExhibitions && upcomingExhibitions?.length > 0 && (
        <S.ExhibitionPeriodTitle>
          {exhibitionListLabel ??
            formatMessage(boutiqueMessages.upcomingExhibitionsTitle)}
        </S.ExhibitionPeriodTitle>
      )}
      {upcomingExhibitions && upcomingExhibitions?.length > 0 && (
        <UpcomingExhibitionItemsContainer
          loadMoreLabel={loadMoreLabel}
          upcomingExhibitions={upcomingExhibitions as ExhibitionItem[]}
        />
      )}
      {pastExhibitions && pastExhibitions?.length > 0 && (
        <S.ExhibitionPeriodTitle>
          {pastExhibitionsLabel ??
            formatMessage(boutiqueMessages.pastExhibitionsTitle)}
        </S.ExhibitionPeriodTitle>
      )}
      {pastExhibitions && pastExhibitions?.length > 0 && (
        <PastExhibitionItemsContainer
          pastExhibitions={pastExhibitions as ExhibitionItem[]}
        />
      )}
    </CMSSection>
  );
};
